import { useState, useCallback, useRef, type ReactNode } from "react"
import type { IconNames } from "@/v2-ui/icon/icon.types"
import Popover from "@/v2-ui/popover"
import type { ButtonProps } from "@/v2-backoffice-client/components/button"
import TooltipButton, {
  type TooltipButtonProps,
} from "@/v2-backoffice-client/components/button/TooltipButton"
import Button from "@/v2-backoffice-client/components/button/Button"

export type ConfirmButtonProps = Omit<TooltipButtonProps, "onClick"> & {
  confirmContent?: ReactNode
  confirmVariant?: ButtonProps["variant"]
  confirmIconSrc?: IconNames
  onConfirm(): void | Promise<void>
}

function ConfirmButton(props: ConfirmButtonProps) {
  const {
    confirmContent = "Are you sure?",
    confirmVariant,
    confirmIconSrc = "check",
    onConfirm,
    variant,
    iconSrc,
    content,
    ...rest
  } = props

  const [isPending, setIsPending] = useState(false)
  // Ref mirrors state to avoid stale-closure issues inside handleClick,
  // since onPointerDownOutside and onClick fire in separate event batches.
  const isPendingRef = useRef(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const setPending = (value: boolean) => {
    isPendingRef.current = value
    setIsPending(value)
  }

  const handleClick = useCallback(async () => {
    if (isPendingRef.current) {
      setPending(false)
      await onConfirm()
    } else {
      setPending(true)
    }
  }, [onConfirm])

  return (
    <Popover
      open={isPending}
      onOpenChange={(open) => {
        if (!open) setPending(false)
      }}
    >
      <Popover.Anchor asChild>
        <TooltipButton
          ref={anchorRef}
          {...rest}
          variant={isPending ? (confirmVariant ?? variant) : variant}
          iconSrc={isPending ? confirmIconSrc : iconSrc}
          content={content}
          // Keep tooltip mounted (stable anchor ref for popover positioning).
          // Force it closed while pending instead of unmounting.
          open={isPending ? false : undefined}
          onClick={handleClick}
        />
      </Popover.Anchor>

      <Popover.Content
        className="ui-confirm-button-popover"
        side="top"
        align="center"
        sideOffset={6}
        size="fit"
        spacing={false}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
        }}
        // Prevent Radix from closing the popover when the user clicks the
        // button itself — let handleClick manage state instead.
        onPointerDownOutside={(e) => {
          if (anchorRef.current?.contains(e.target as Node)) {
            e.preventDefault()
          }
        }}
      >
        {confirmContent}

        <Button
          className="ml-1 text-xs"
          variant="outline"
          size="tiny"
          onClick={() => {
            setPending(false)
          }}
        >
          Cancel
        </Button>
      </Popover.Content>
    </Popover>
  )
}

export default ConfirmButton
